import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdKeyboardArrowDown } from 'react-icons/md';
import {
  heroNameVariant,
  heroNameLineVariant,
  heroTitleVariant,
  heroSubtitleVariant,
  heroButtonsVariant,
  scrollDownVariant,
  scrollDownHoverVariant,
} from '../../animations/animations';
import ResumeModal from '../UI/ResumeModal';
import TerminalBlock from '../UI/TerminalBlock';
import ChipGraphic from '../UI/ChipGraphic';

const Hero = () => {
  const [showScrollDown, setShowScrollDown] = useState(true);
  const [isResumeOpen, setIsResumeOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollDown(window.scrollY < 100);
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToAbout = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const subtitles = [
    { icon: '⚙️', text: 'Build.' },
    { icon: '🔍', text: 'Debug.' },
    { icon: '🚀', text: 'Ship.' },
  ];

  return (
    <section className="min-h-screen flex flex-col items-center justify-start pt-28 pb-12 px-6 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primaryLight/5 rounded-full blur-3xl" />
      </div>

      {/* Chip graphic — parallax decoration */}
      <ChipGraphic scrollY={scrollY} />

      <div className="max-w-4xl mx-auto text-center z-10">
        {/* Main Title */}
        <motion.div
          variants={heroNameVariant}
          initial="initial"
          animate="animate"
          className="mb-4"
        >
          <h1 className="text-5xl md:text-7xl font-bold gradient-text">
            Suhas P
          </h1>
          <motion.div
            variants={heroNameLineVariant}
            initial="initial"
            animate="animate"
            className="h-1 w-32 md:w-48 bg-gradient-to-r from-primary to-primaryLight mx-auto mt-4 rounded-full"
          />
        </motion.div>

        {/* Subtitle Role */}
        <motion.h2
          variants={heroTitleVariant}
          initial="initial"
          animate="animate"
          className="text-xl md:text-2xl text-textSecondary mt-6 mb-8"
        >
          Embedded Linux &amp; Firmware Engineer
        </motion.h2>

        {/* Animated subtitles */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-8">
          {subtitles.map((item, index) => (
            <motion.span
              key={index}
              variants={heroSubtitleVariant(index)}
              initial="initial"
              animate="animate"
              className="text-lg md:text-xl text-textPrimary"
            >
              <span className="mr-2">{item.icon}</span>
              {item.text}
            </motion.span>
          ))}
        </div>

        {/* Description */}
        <motion.p
          variants={heroSubtitleVariant(3)}
          initial="initial"
          animate="animate"
          className="text-textMuted max-w-2xl mx-auto mb-8 leading-relaxed"
        >
          I write firmware that runs 24/7 on production hardware. My background is in Electrical
          and Electronics Engineering - I don't just see the code, I see the hardware underneath it.
          Over 3 years I have owned embedded Linux systems end-to-end: cross-compiler toolchains,
          custom rootfs, secure OTA updates, reverse engineering OEM stacks with no documentation,
          and keeping a fleet of ASIC devices at 99.9% uptime.
        </motion.p>

        {/* Terminal Block */}
        <TerminalBlock />

        {/* CTA Buttons */}
        <motion.div
          variants={heroButtonsVariant}
          initial="initial"
          animate="animate"
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a href="#projects" className="btn-primary">
            View Projects
          </a>

          <button
            onClick={() => setIsResumeOpen(true)}
            className="btn-secondary"
          >
            Resume
          </button>
        </motion.div>

        {/* Email */}
        <motion.div
          variants={heroSubtitleVariant(5)}
          initial="initial"
          animate="animate"
          className="mt-8"
        >
          <a
            href="mailto:suhas@suhasp.dev"
            className="text-primaryLight hover:text-primary transition-colors"
          >
            suhas@suhasp.dev
          </a>
        </motion.div>
      </div>

      {/* Scroll Down Indicator */}
      <AnimatePresence>
        {showScrollDown && (
          <motion.button
            variants={scrollDownVariant}
            initial="initial"
            animate="animate"
            exit="exit"
            whileHover="hover"
            onClick={scrollToAbout}
            className="fixed bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center text-textSecondary hover:text-primary transition-colors cursor-pointer z-30"
          >
            <span className="text-sm mb-2">Scroll Down</span>
            <motion.div variants={scrollDownHoverVariant}>
              <MdKeyboardArrowDown size={28} />
            </motion.div>
          </motion.button>
        )}
      </AnimatePresence>

      <ResumeModal isOpen={isResumeOpen} onClose={() => setIsResumeOpen(false)} />
    </section>
  );
};

export default Hero;
